import { useCallback, useEffect, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

/**
 * A single worker on the Supabase comms canary allowlist ("cohort").
 * Keyed by `user_id` — the allowlist is scoped to users, not workers.
 */
export interface AllowlistEntry {
  user_id: number;
  worker_id: number | null;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  enabled_at: string | null;
  // graphy user.id of the admin who enabled the worker (null for rake/system).
  enabled_by: number | null;
}

interface ListResponse {
  success: boolean;
  data: { entries: AllowlistEntry[] };
  errors?: string[];
}

interface AddResponse {
  success: boolean;
  data: { entry: AllowlistEntry };
  errors?: string[];
}

function extractError(err: unknown, fallback: string): string {
  return (
    (
      err as { response?: { data?: { errors?: string[] } } }
    )?.response?.data?.errors?.join(', ') ??
    (err as Error)?.message ??
    fallback
  );
}

/**
 * Fetches the current Supabase cohort. Returns the entries plus loading/error
 * state and a `refetch` to re-pull after a mutation.
 */
export function useCommsAllowlist() {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllowlist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosRubyClient.get<ListResponse>(
        '/api/admin/comms/allowlist',
      );
      setEntries(res.data.data?.entries ?? []);
    } catch (err: unknown) {
      captureSupabaseError(err, { action: 'fetch_allowlist' });
      setError(extractError(err, 'Failed to load the Supabase cohort'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAllowlist();
  }, [fetchAllowlist]);

  return { entries, loading, error, refetch: fetchAllowlist };
}

/**
 * Adds a worker to the cohort. Pass the worker's `user.id` (the allowlist is
 * keyed by user_id, NOT worker id). Returns the created entry.
 */
export function useAddToAllowlist() {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useCallback(async (userId: number): Promise<AllowlistEntry> => {
    setAdding(true);
    setError(null);
    try {
      const res = await axiosRubyClient.post<AddResponse>(
        '/api/admin/comms/allowlist',
        { user_id: userId },
      );
      return res.data.data.entry;
    } catch (err: unknown) {
      const message = extractError(err, 'Failed to add worker to the cohort');
      captureSupabaseError(err, {
        action: 'add_to_allowlist',
        extra: { user_id: userId },
      });
      setError(message);
      throw err;
    } finally {
      setAdding(false);
    }
  }, []);

  return { add, adding, error };
}

/**
 * Removes a worker from the cohort by `user_id`.
 */
export function useRemoveFromAllowlist() {
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (userId: number): Promise<void> => {
    setRemovingId(userId);
    setError(null);
    try {
      await axiosRubyClient.delete(`/api/admin/comms/allowlist/${userId}`);
    } catch (err: unknown) {
      const message = extractError(
        err,
        'Failed to remove worker from the cohort',
      );
      captureSupabaseError(err, {
        action: 'remove_from_allowlist',
        extra: { user_id: userId },
      });
      setError(message);
      throw err;
    } finally {
      setRemovingId(null);
    }
  }, []);

  return { remove, removingId, error };
}
